import {
  Component,
  computed,
  effect,
  inject,
  OnDestroy,
  Signal,
} from '@angular/core';
import { FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  BankDataId,
  BicString,
  CONFIGURATION_SETTING_KEYS,
  CreditInstituteString,
  EEntityType,
  EOrderOrigin,
  EPaymentProviders,
  ERelationType,
  IBankData,
  IbanString,
  IPaymentPreference,
  PaymentMethod,
} from '@web/dtos';
import { QuillViewComponent } from 'ngx-quill';
import { Subscription } from 'rxjs';
import { PaymentMethodDisplayComponent } from '../../../../components/data-display/payment-method-display/payment-method-display.component';
import { SingleSelectComponent } from '../../../../components/data-input/form-inputs/single-select/single-select.component';
import { ButtonComponent } from '../../../../components/feedback/button/button.component';
import { SectionHeaderActionsComponent } from '../../../../components/layout/section/section-header/section-header-actions/section-header-actions.component';
import { SectionHeaderComponent } from '../../../../components/layout/section/section-header/section-header.component';
import { SectionHeadlineComponent } from '../../../../components/layout/section/section-header/section-headline/section-headline.component';
import { SectionComponent } from '../../../../components/layout/section/section.component';
import { FilterTextsPipe } from '../../../../pipes/filterTexts/filterTexts.pipe';
import { LanguagePipe } from '../../../../pipes/language/language.pipe';
import { ConfigurationStore } from '../../../../services/api/configuration/configuration-store.service';
import { PaymentProviderStore } from '../../../../services/api/payment-provider/payment-provider-store.service';
import { PaymentPreferenceStore } from '../../../../services/api/user/paymentPreference/payment-preference-store.service';
import { UserStore } from '../../../../services/api/user/user/user-store.service';
import { CorporateDesignService } from '../../../../services/corporate-design/corporate-design.service';
import { BankDataStore } from '../../../../services/api/bank-data/bank-data.store';
import { NgClass } from '@angular/common';
import { DialogService } from '../../../../services/support/dialog/dialog.service';
import { BankDataDialogComponent } from '../../../../components/dialogs/bank-data-dialog/bank-data-dialog.component';
import { WebTextStore } from '../../../../services/api/text/web-text-store.service';
import { SubscriptionPaymentPreferenceStore } from '../../../../services/api/user/subscriptionPaymentPreference/subscription-payment-preference.store';
import { LoadingAnimationComponent } from '../../../../components/feedback/loading-animation/loading-animation.component';
import { DeletePaymentMethodDialogComponent } from '../../../../components/dialogs/delete-payment-method-dialog/delete-payment-method-dialog.component';
import { EConfigurationChannel } from '@web/dtos';

@Component({
  selector: 'aw-account-ticket-payment-page',
  imports: [
    SectionComponent,
    ReactiveFormsModule,
    ButtonComponent,
    SectionHeaderActionsComponent,
    SectionHeaderComponent,
    SectionHeadlineComponent,
    FilterTextsPipe,
    LanguagePipe,
    QuillViewComponent,
    SingleSelectComponent,
    PaymentMethodDisplayComponent,
    NgClass,
    LoadingAnimationComponent,
  ],
  templateUrl: './account-payment-page.component.html',
})
export class AccountPaymentPageComponent implements OnDestroy {
  protected readonly configurationStore: ConfigurationStore =
    inject(ConfigurationStore);
  protected readonly paymentProviderStore: PaymentProviderStore =
    inject(PaymentProviderStore);
  protected readonly paymentPreferenceStore: PaymentPreferenceStore = inject(
    PaymentPreferenceStore
  );
  readonly subscriptionPreferenceStore: SubscriptionPaymentPreferenceStore =
    inject(SubscriptionPaymentPreferenceStore);
  readonly textStore: WebTextStore = inject(WebTextStore);
  readonly bankDataStore: BankDataStore = inject(BankDataStore);
  readonly userStore: UserStore = inject(UserStore);
  readonly corporateDesignService: CorporateDesignService = inject(
    CorporateDesignService
  );
  readonly dialogService: DialogService = inject(DialogService);
  readonly fb: FormBuilder = inject(FormBuilder);

  protected readonly paymentMethods: Signal<PaymentMethod[]> =
    this.paymentProviderStore.paymentMethods;
  protected readonly isLoadingDirectPayments: Signal<boolean> = computed(
    () =>
      this.paymentPreferenceStore.isLoading() ||
      this.paymentProviderStore.isLoadingPaymentMethods()
  );

  public readonly preferenceControl: FormControl<string> =
    this.fb.nonNullable.control<string>('');
  public readonly subscriptionPreferenceControl: FormControl<BankDataId> =
    this.fb.nonNullable.control<BankDataId>('' as BankDataId);

  protected readonly EPaymentProviders: typeof EPaymentProviders =
    EPaymentProviders;
  protected readonly ERelationType: typeof ERelationType = ERelationType;
  protected readonly EEntityType: typeof EEntityType = EEntityType;

  private readonly subscription: Subscription = new Subscription();

  constructor() {
    effect(() => {
      const userId: string | undefined = this.userStore.user()?.userId;
      if (userId) {
        this.paymentPreferenceStore.loadPreference(userId);
        this.paymentProviderStore.getPaymentMethods();
        this.subscriptionPreferenceStore.loadSubscriptionPaymentPreference();
        this.bankDataStore.getBankData();
      }
    });
    effect(() => {
      const paymentPreference: IPaymentPreference | undefined =
        this.paymentPreferenceStore.paymentPreference();
      if (paymentPreference) {
        this.preferenceControl.setValue(paymentPreference.paymentMethodId, {
          emitEvent: false,
        });
      }
    });

    effect(() => {
      const subscriptionPaymentPreference: BankDataId =
        this.subscriptionPreferenceStore.subscriptionPaymentPreference();
      if (subscriptionPaymentPreference) {
        this.subscriptionPreferenceControl.setValue(
          subscriptionPaymentPreference,
          {
            emitEvent: false,
          }
        );
      }
    });
    this.subscription.add(
      this.preferenceControl.valueChanges.subscribe((value: string) => {
        const userId: string | undefined = this.userStore.user()?.userId;
        if (value && userId) {
          const preference: IPaymentPreference = {
            id: this.paymentPreferenceStore.paymentPreference()?.id,
            userId,
            paymentMethodId: value,
            paymentProvider: this.configurationStore.getEnumConfiguration(
              CONFIGURATION_SETTING_KEYS.PaymentProvider,
              EPaymentProviders
            ),
          };
          this.paymentPreferenceStore.updatePreference(preference);
        }
      })
    );

    this.subscription.add(
      this.subscriptionPreferenceControl.valueChanges.subscribe(
        (bankDataId: BankDataId) => {
          if (bankDataId) {
            this.subscriptionPreferenceStore.updateSubscriptionPaymentPreference(
              bankDataId
            );
          }
        }
      )
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  addPaymentDirect(): void {
    this.paymentProviderStore
      .addPaymentMethods(EOrderOrigin.AccountPage, this.userStore.user())
      .subscribe(response => {
        window.location.href = response.data.redirectUrl;
      });
  }

  deletePaymentDirect(id: string): void {
    this.paymentProviderStore.deletePaymentMethods(id).subscribe(response => {
      this.paymentProviderStore.reloadMethods();
      window.location.href = response.data.redirectUrl;
    });
  }

  openDeletePaymentMethodDialog(id: string): void {
    const dialogRef = this.dialogService.openDialog(
      DeletePaymentMethodDialogComponent,
      {
        headline: '',
        width: '800px',
      }
    );

    dialogRef.closed.subscribe(result => {
      if (result != undefined && result.submit) {
        this.deletePaymentDirect(id);
      }
    });
  }

  openBankDataDialog(): void {
    this.dialogService.openDialog<{
      bankData: IBankData;
    }>(BankDataDialogComponent, {
      headline: '',
      width: '800px',
      bankData: {
        firstName: '',
        lastName: '',
        iban: '' as IbanString,
        bic: '' as BicString,
        creditInstitute: '' as CreditInstituteString,
      },
    });
  }

  protected readonly CONFIGURATION_SETTING_KEYS = CONFIGURATION_SETTING_KEYS;
  protected readonly EConfigurationChannel = EConfigurationChannel;
}
